"use client";

/**
 * The one-CTA-per-empty-state primitive (copy-voice-overhaul-2026-08-17 T6,
 * canon rule 4: name what will live here -> exactly one CTA). Fires
 * `empty_state_cta_clicked` (props: surface, locale) on click — the
 * escape-rate metric (no liveness row: without a render event, zero clicks
 * cannot be told apart from zero renders). `surface` is a short stable identifier for the
 * empty state (e.g. "digest_no_issues"), NOT the page route — lets each
 * surface's click-through be queried independently.
 */

import Link from "next/link";
import type { ReactNode } from "react";
import { useAnalytics } from "@/lib/analytics/hooks";

export type EmptyStateCtaProps = {
	href: string;
	locale: string;
	surface: string;
	children: ReactNode;
	className?: string;
};

const DEFAULT_CLASS =
	"inline-flex w-fit items-center gap-1.5 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

export function EmptyStateCta({
	href,
	locale,
	surface,
	children,
	className,
}: EmptyStateCtaProps) {
	const { trackEmptyStateCtaClicked } = useAnalytics();

	return (
		<Link
			href={href}
			data-testid="empty-state-cta"
			onClick={() => trackEmptyStateCtaClicked({ surface, locale })}
			className={className ?? DEFAULT_CLASS}
		>
			{children}
		</Link>
	);
}
